import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Text,
  HStack,
  FormControl,
  FormLabel,
  Select,
  VStack,
} from '@chakra-ui/react'
import { Clock3, Save } from 'lucide-react'
import type { ParentTicketStatus } from '@/types/parentTicket'
import { useThemeColors } from '@/hooks/useThemeColors'

type Option = { label: string; value: string }

type Props = {
  statusLabel: string
  slaDueAt: string | null
  statusValue: ParentTicketStatus
  priorityValue?: string
  statusOptions: Option[]
  priorityOptions?: Option[]
  onChangeStatus: (value: string) => void
  onChangePriority?: (value: string) => void
  isReadOnly?: boolean
  hidePriority?: boolean
  /** Called when user clicks Update to persist the current status (no auto-update on dropdown change) */
  onUpdateStatus?: () => void
  /** Show loading spinner on Save button while status is being saved */
  isSavingStatus?: boolean
  /** Opens add/comment update modal (e.g. for Activity Log header) */
  onAddComment?: () => void
  /** Computed total ticket time (e.g. "2 days 3 hours 15 min") */
  totalTicketTime?: string | null
  /** Omit outer card chrome; use inside a parent card (e.g. parent ticket view) */
  embedded?: boolean
  /** Hide live clock (e.g. ticket Overview action rail) */
  hideLiveClock?: boolean
  /** Hide embedded section title when wrapped by a parent card */
  hideSectionTitle?: boolean
}


function formatLiveDateTime(ts: number): string {
  return new Date(ts).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export default function StatusSlaCard({
  statusLabel,
  slaDueAt,
  statusValue,
  priorityValue,
  statusOptions,
  priorityOptions,
  onChangeStatus,
  onChangePriority,
  isReadOnly = false,
  hidePriority = false,
  onUpdateStatus,
  isSavingStatus = false,
  onAddComment,
  totalTicketTime,
  embedded = false,
  hideLiveClock = false,
  hideSectionTitle = false,
}: Props) {
  const colors = useThemeColors()

  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const body = (
    <VStack spacing={embedded ? 3 : 4} align="stretch" p={embedded ? 0 : 4}>
        {!hideLiveClock && (
          <HStack spacing={2} align="center">
            <Box
              w="28px"
              h="28px"
              display="grid"
              placeItems="center"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
              bg="blue.50"
            >
              <Clock3 size={16} color="#3182ce" />
            </Box>
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              {formatLiveDateTime(now)}
            </Text>
          </HStack>
        )}

        {totalTicketTime && (
          <Box>
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
              Total ticket time
            </Text>
            <Text fontSize="sm" color="gray.700">
              {totalTicketTime}
            </Text>
          </Box>
        )}

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
            Status:
          </FormLabel>
          <Select
            value={statusValue}
            onChange={(e) => onChangeStatus(e.target.value)}
            size="md"
            h="44px"
            borderRadius="md"
            borderColor="gray.200"
            bg="white"
            isDisabled={isReadOnly}
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormControl>

        {!hidePriority && priorityOptions && onChangePriority && (
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
              Priority:
            </FormLabel>
            <Select
              value={priorityValue}
              onChange={(e) => onChangePriority(e.target.value)}
              size="md"
              h="44px"
              borderRadius="md"
              borderColor="gray.200"
              bg="white"
              isDisabled={isReadOnly}
            >
              {priorityOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {onUpdateStatus && (
          <Button
            leftIcon={<Save size={16} />}
            colorScheme="blue"
            size="sm"
            w="full"
            onClick={onUpdateStatus}
            isLoading={isSavingStatus}
            loadingText="Saving..."
          >
            Save
          </Button>
        )}
    </VStack>
  )

  if (embedded) {
    return (
      <Box>
        {!hideSectionTitle && (
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.600"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={2}
          >
            Ticket actions
          </Text>
        )}
        {body}
      </Box>
    )
  }

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
    >
      <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
        <Text fontWeight="700" color={colors.primary}>
          Ticket Actions
        </Text>
      </Box>
      {body}
    </Box>
  )
}
